<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import { computed, reactive, ref } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import FormProcess from '@/models/formProcess.ts'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { DURATION_UNLIMITED } from '@/types/ActionDuration.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const shipActionStore = useShipActionStore()
const formProcess = new FormProcess()

// Repair action utility functions
interface RepairActionValidationError extends Error {
  code: 'INVALID_DURATION' | 'DURATION_TOO_LONG'
}

/**
 * Validates a repair action duration
 * @param duration The duration to validate
 * @throws {RepairActionValidationError} When validation fails
 */
function validateRepairDuration(duration: number): void {
  if (duration < 0) {
    const error = new Error('Duration cannot be negative') as RepairActionValidationError
    error.code = 'INVALID_DURATION'
    throw error
  }
  if (duration > 365 && duration !== DURATION_UNLIMITED) {
    const error = new Error('Duration too long (max 365 days)') as RepairActionValidationError
    error.code = 'DURATION_TOO_LONG'
    throw error
  }
}

/**
 * Handles backend error responses for repair actions
 * @param error The error from the backend
 * @returns User-friendly error message
 */
function handleRepairActionError(error: any): string {
  if (error.response?.data?.message) {
    const message = error.response.data.message
    if (message.includes('Duration')) {
      return 'Invalid duration specified'
    }
    return message
  }
  return 'An error occurred while setting the repair action'
}

// Form state
const repairDuration = ref('full') // 'full' or number as string

// Computed values

// Generate repair duration options from 5 to 200 days in steps of 5
const repairOptions = computed(() => {
  const options = [{ value: 'full', label: t('modals.repairAction.untilFullyRepaired') }]

  // Add options from 5 to 200 days in steps of 5
  for (let days = 5; days <= 200; days += 5) {
    options.push({
      value: days.toString(),
      label: t('modals.repairAction.repairForDays', { days })
    })
  }

  return options
})

// Calculate duration based on selection
const duration = computed(() => {
  if (repairDuration.value === 'full') {
    return DURATION_UNLIMITED
  } else {
    return parseInt(repairDuration.value)
  }
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: ''
})

// Handle form submission
const submitRepair = () => {
  try {
    formProcess.resetFormErrors(formErrors)
    
    // Validate duration
    validateRepairDuration(duration.value)

    // Set pending action to repair
    shipActionStore.setPendingAction('repair')
    
    // Queue the repair action with duration
    shipActionStore.queuePendingAction(
      shipActionStore.pathEndX,
      shipActionStore.pathEndY,
      0,
      0,
      duration.value
    )
    
    gameStore.modalType = ''
    
  } catch (error: any) {
    if (error.code === 'INVALID_DURATION') {
      formErrors.generic = t('modals.repairAction.errors.invalidDuration')
    } else if (error.code === 'DURATION_TOO_LONG') {
      formErrors.generic = t('modals.repairAction.errors.durationTooLong')
    } else {
      formErrors.generic = handleRepairActionError(error)
    }
  }
}
</script>

<template>
  <SmallModel :title="t('modals.repairAction.title')">
    <div class="p-4">
      <div class="mb-4">
        <div class="text-subtext mb-3">
          {{ t('modals.repairAction.description') }}
        </div>

        <!-- Repair duration selector -->
        <div class="mb-4">
          <FormSelect
            v-model="repairDuration"
            :options="repairOptions"
            :label="t('modals.repairAction.durationLabel')"
          />
        </div>

        <!-- Duration preview -->
        <div v-if="duration === DURATION_UNLIMITED" class="mb-4 p-3 bg-metal-800 rounded">
          <div class="text-sm text-metal-300">
            <strong>{{ t('modals.repairAction.duration') }}</strong> {{ t('modals.repairAction.durationUnlimited') }}
          </div>
          <div class="text-xs text-subtext mt-1">
            {{ t('modals.repairAction.shipsWillRepairFull') }}
          </div>
        </div>

        <div v-else class="mb-4 p-3 bg-metal-800 rounded">
          <div class="text-sm text-metal-300">
            <strong>{{ t('modals.repairAction.duration') }}</strong> {{ t('modals.repairAction.durationDays', { days: duration }) }}
          </div>
          <div class="text-xs text-subtext mt-1">
            {{ t('modals.repairAction.shipsWillRepairDays', { days: duration }) }}
          </div>
        </div>
      </div>

      <div class="flex justify-between pt-2">
        <FormGenericError :error="formErrors.generic" />
        <div class="flex gap-2">
          <ActionButton @click="gameStore.modalType = ''" class="bg-metal-600 hover:bg-metal-500">
            {{ t('modals.repairAction.cancel') }}
          </ActionButton>
          <ActionButton @click="submitRepair">
            {{ t('modals.repairAction.queueRepair') }}
          </ActionButton>
        </div>
      </div>
    </div>
  </SmallModel>
</template>

<style scoped lang="scss">
select {
  @apply bg-metal-800 border border-metal-600 rounded focus:ring-blue-500 focus:border-blue-500;
}
</style> 